import * as http from '../api/http'
import {CATALOGOS_TYPES} from '../constants/catalogos.types'
import {generalOpenModalError, generalShowLoader} from './general.actions'
import {Dispatch} from 'react'
import {ENDPOINTS} from '../api/config'
import {KEYS} from '../constants/keysStorage'
import {CatalogoAction, CatalogosI} from '../interfaces/catalogos.interface'
import {State} from '../reducers'

type NombreCatalogo = keyof CatalogosI

interface MarcaCatalogo {
  version: string
  instante: number
}

const MILISEGUNDOS_VIGENCIA_CATALOGO = 5 * 60 * 1000

const marcasCatalogo: Record<string, MarcaCatalogo> = {}

const peticionesEnVuelo: Record<string, Promise<any>> = {}

const versionNormativaDeLaEmpresa = (estado: State): string => {
  const emisor: any = estado?.catalogos?.datosEmisor
  const version = emisor?.versionhacienda ?? emisor?.versionHacienda
  if (version === null || version === undefined) return ''
  return `${version}`.trim()
}

const hayDatosEnCache = (nombre: NombreCatalogo, estado: State): boolean => {
  const datos: any = (estado?.catalogos as any)?.[nombre]
  return Array.isArray(datos) ? datos.length > 0 : false
}

const catalogoVigente = (nombre: NombreCatalogo, estado: State, forzar: boolean): boolean => {
  if (forzar) return false
  if (!hayDatosEnCache(nombre, estado)) return false

  const marca = marcasCatalogo[nombre]
  if (!marca) return false
  if (marca.version !== versionNormativaDeLaEmpresa(estado)) return false

  return Date.now() - marca.instante < MILISEGUNDOS_VIGENCIA_CATALOGO
}

const sellarCatalogo = (nombre: NombreCatalogo, estado: State) => {
  marcasCatalogo[nombre] = {
    version: versionNormativaDeLaEmpresa(estado),
    instante: Date.now(),
  }
}

export const olvidarCatalogosCargados = () => {
  Object.keys(marcasCatalogo).forEach((nombre) => delete marcasCatalogo[nombre])
  Object.keys(peticionesEnVuelo).forEach((nombre) => delete peticionesEnVuelo[nombre])
}

const enVuelo = (nombre: NombreCatalogo): Promise<any> | undefined => peticionesEnVuelo[nombre]

const registrarPeticion = async <T>(nombre: NombreCatalogo, peticion: Promise<T>): Promise<T> => {
  peticionesEnVuelo[nombre] = peticion as Promise<any>

  try {
    return await peticion
  } finally {
    delete peticionesEnVuelo[nombre]
  }
}

const unaSolaPeticion = <T>(nombre: NombreCatalogo, ejecutar: () => Promise<T>): Promise<T> => {
  const enCurso = enVuelo(nombre)
  if (enCurso) return enCurso as Promise<T>

  return registrarPeticion(nombre, ejecutar())
}

const CATALOGO_DATOS_EMISOR: NombreCatalogo = 'datosEmisor'

const hayDatosEmisorEnCache = (estado: State): boolean => {
  const emisor: any = estado?.catalogos?.datosEmisor
  return !!emisor && Object.keys(emisor).length > 0
}

const datosEmisorVigentes = (estado: State, forzar: boolean): boolean => {
  if (forzar) return false
  if (!hayDatosEmisorEnCache(estado)) return false

  const marca = marcasCatalogo[CATALOGO_DATOS_EMISOR]
  if (!marca) return false

  return Date.now() - marca.instante < MILISEGUNDOS_VIGENCIA_CATALOGO
}

export const invalidarDatosEmisor = () => {
  delete marcasCatalogo[CATALOGO_DATOS_EMISOR]
}

const emisorConTerritorioExpandido = (cuerpo: any) => ({
  ...cuerpo,
  departamento: {
    idDepartamento: cuerpo?.departamento,
    valor: cuerpo?.departamentonombre,
  },
  municipio: {
    idMmunicipio: cuerpo?.municipio,
    valor: cuerpo?.municipionombre,
  },
})

// SETTERS ---------------------------------------------------------------
export const handleSetCondicionesPago = (condiciones: any) => ({type: CATALOGOS_TYPES.CATALOGO_SET_CONDICIONES_PAGO, payload: condiciones})
export const handleSetFormasPago = (formas: any) => ({type: CATALOGOS_TYPES.CATALOGO_SET_FORMAS_PAGO, payload: formas})
export const handleSetPlazosPago = (plazos: any) => ({type: CATALOGOS_TYPES.CATALOGO_SET_PLAZOS_PAGO, payload: plazos})
export const handleSetTipoCargosMembresia = (cargos: any) => ({type: CATALOGOS_TYPES.CATALOGO_SET_TIPO_CARGOS_MEMBRESIA, payload: cargos})
export const handleSetProductos = (productos: any) => ({type: CATALOGOS_TYPES.CATALOGO_SET_PRODUCTOS, payload: productos})

export const setDepartamentos = (value: any) => ({
  type: CATALOGOS_TYPES.CATALOGO_SET_DEPARTAMENTOS,
  payload: value,
})
export const setMunicipios = (value: any) => ({
  type: CATALOGOS_TYPES.CATALOGO_SET_MUNICIPIOS,
  payload: value,
})
export const setDistritos = (value: any) => ({
  type: CATALOGOS_TYPES.CATALOGO_SET_DISTRITOS,
  payload: value,
})
export const setMapaDistritoMunicipio = (departamento: string, mapa: Record<string, string>) => ({
  type: CATALOGOS_TYPES.CATALOGO_SET_MAPA_DISTRITO_MUNICIPIO,
  payload: {departamento, mapa},
})
export const setPlanFacturas = (value: any) => ({
  type: CATALOGOS_TYPES.CATALOGO_SET_PLANFACTURAS,
  payload: value,
})
export const setTipoEstablecimientos = (value: any) => ({
  type: CATALOGOS_TYPES.CATALOGO_SET_TIPO_ESTABLECIMIENTOS,
  payload: value,
})
export const setTipoPersona = (value: any) => ({
  type: CATALOGOS_TYPES.CATALOGO_SET_TIPO_PERSONA,
  payload: value,
})
export const setActividadesEconomicas = (value: any) => ({
  type: CATALOGOS_TYPES.CATALOGO_SET_ACTIVIDADES_ECONOMICAS,
  payload: value,
})
export const setTiposDocumento = (value: any) => ({
  type: CATALOGOS_TYPES.CATALOGO_SET_TIPO_DOCUMENTO,
  payload: value,
})
export const setUnidadesMedida = (value: any) => ({
  type: CATALOGOS_TYPES.CATALOGO_SET_UNIDADES_MEDIDA,
  payload: value,
})
export const setTiposDocumentoEmision = (value: any) => ({
  type: CATALOGOS_TYPES.CATALOGO_SET_TIPO_DOCUMENTOS_EMISION,
  payload: value,
})
export const setDatosGeneralesEmisor = (datos: any) => ({
  type: CATALOGOS_TYPES.GENERAL_DATOS_EMISOR,
  payload: datos,
})
export const setCatalogoBotonesAyuda = (data: any) => ({
  type: CATALOGOS_TYPES.CATALOGO_SET_BOTONES_AYUDA,
  payload: data
})
export const setCatalogoBancos = (data: any) => ({
  type: CATALOGOS_TYPES.CATALOGO_SET_BANCOS,
  payload: data
})
export const setCatalogoCondicionOperacionCat016 = (data: any) => ({
  type: CATALOGOS_TYPES.CATALOGO_SET_CONDICION_OPERACION_CAT016,
  payload: data
})

export const catalogosClear = () => ({
  type: CATALOGOS_TYPES.CATALOGOS_CLEAR,
  payload: null,
})

export const setServiciosAdicionales = (value: any) => ({
  type: CATALOGOS_TYPES.CATALOGO_SET_SERVICIOS_ADICIONALES,
  payload: value,
})
export const setImpuestosDistintosAlIva = (value: any) => ({
  type: CATALOGOS_TYPES.CATALOGO_SET_IMPUESTOS,
  payload: value,
})
// GETS -----------------------------------------------------------------
export const getDatosEmisor =
  (forzar: boolean = false) =>
  async (dispatch: Dispatch<CatalogoAction>, getState: () => State) => {
    if (datosEmisorVigentes(getState(), forzar)) return true

    return await unaSolaPeticion(CATALOGO_DATOS_EMISOR, () =>
      http
        .get(ENDPOINTS.FACTURACION_GET_EMPRESA_EMISOR, {}, KEYS.TOKEN)
        .then(async (res: any) => {
          if (res?.message) {
            dispatch(generalOpenModalError(res.message))
            return false
          }

          dispatch(setDatosGeneralesEmisor(emisorConTerritorioExpandido(res?.body)))
          sellarCatalogo(CATALOGO_DATOS_EMISOR, getState())
          return true
        })
        .catch(async (err: any) => {
          let message = 'No se logró obtener los datos del Emisor'
          await dispatch(
            generalOpenModalError(
              err?.response?.data?.message ?? message,
              err?.response?.data?.status
            )
          )
          return false
        })
    )
  }

export const getBancos =
  (forzar: boolean = false) =>
  async (dispatch: Dispatch<CatalogoAction>, getState: () => State) => {
    if (catalogoVigente('bancos', getState(), forzar)) return true
    const enCurso = enVuelo('bancos')
    if (enCurso) return await enCurso

    const peticion = http
      .get(ENDPOINTS.GET_BANCOS)
      .then(async (res: any) => {
        if (res?.data) {
          dispatch(setCatalogoBancos(res?.data))
          sellarCatalogo('bancos', getState())
          return true
        } else {
          dispatch(
            generalOpenModalError('No hay bancos disponibles para realizar operaciones')
          )
          return false
        }
      })
      .catch(async (err: any) => {
        let message = 'Error al cargar catalogo de bancos, intente nuevamente'
        await dispatch(
          generalOpenModalError(
            err?.response?.data?.message ?? message,
            err?.response?.data?.status
          )
        )
        return false
      })
    return await registrarPeticion('bancos', peticion)
  }

  export const getServiciosAdicionales =
  () => async (dispatch: Dispatch<CatalogoAction>) => {
    return await http
      .get(ENDPOINTS.GET_SERVICIOS_ADICIONALES)
      .then((res: any) => {
        
        if (res?.data) {
          dispatch(setServiciosAdicionales(res?.data));
          return true;
        } else {
          dispatch(generalOpenModalError('No hay servicios adicionales realizar operaciones'))
          return false;
        }
      })
      .catch((err: any) => {
        let message = 'Error al cargar catalogo de servicios adicionales, intente nuevamente'
        dispatch(generalOpenModalError(err?.response?.data?.message ?? message, err?.response?.data?.status))
        return false;
      })
  }

export const getDepartamentos =
  (forzar: boolean = false) =>
  async (dispatch: Dispatch<CatalogoAction>, getState: () => State) => {
    if (catalogoVigente('departamentos', getState(), forzar)) return true
    const enCurso = enVuelo('departamentos')
    if (enCurso) return await enCurso

    const peticion = http
      .get(ENDPOINTS.GET_DEPARTAMENTOS)
      .then(async (res: any) => {
        if (res.body) {
          dispatch(setDepartamentos(res.body))
          sellarCatalogo('departamentos', getState())
          return true
        } else {
          dispatch(
            generalOpenModalError('No hay departamentos disponibles para realizar operaciones')
          )
          return false
        }
      })
      .catch(async (err: any) => {
        let message = 'Error al cargar catalogo de departamentos, intente nuevamente'
        await dispatch(
          generalOpenModalError(
            err?.response?.data?.message ?? message,
            err?.response?.data?.status
          )
        )
        return false
      })
    return await registrarPeticion('departamentos', peticion)
  }
export const getMunicipios =
  (idDepartamento: number, depa: string, loader: boolean = false) =>
  async (dispatch: Dispatch<CatalogoAction>) => {
    if (loader) dispatch(generalShowLoader(true))
    return await http
      .post(ENDPOINTS.GET_MUNICIPIOS, {
        codigo: idDepartamento < 10 ? '0' + idDepartamento : idDepartamento,
        valor: depa,
      })
      .then(async (res: any) => {
        if (res.body) {
          dispatch(setMunicipios(res.body))
          return true
        } else {
          dispatch(generalOpenModalError('No hay municipios disponibles para realizar operaciones'))
          return false
        }
      })
      .catch(async (err: any) => {
        let message = 'Error al cargar catalogo de municipios, intente nuevamente'
        await dispatch(generalOpenModalError(err?.response?.data?.message ?? message,err?.response?.data?.status))
        return false
      })
      .finally(() => {
        if (loader) dispatch(generalShowLoader(false))
      })
  }
const codigoTerritorial = (codigo: any): string => {
  const valor = `${codigo ?? ''}`.trim()
  if (!valor) return ''
  return valor.length === 1 ? `0${valor}` : valor
}

export const getDistritos =
  (departamento: any, municipio: any, loader: boolean = false) =>
  async (dispatch: Dispatch<CatalogoAction>) => {
    const codigoDepartamento = codigoTerritorial(departamento)
    const codigoMunicipio = codigoTerritorial(municipio)
    if (!codigoDepartamento) {
      dispatch(setDistritos([]))
      return false
    }
    if (loader) dispatch(generalShowLoader(true))
    return await http
      .post(ENDPOINTS.GET_DISTRITO, {
        departamento: codigoDepartamento,
        ...(codigoMunicipio ? {municipio: codigoMunicipio} : {}),
      })
      .then(async (res: any) => {
        const distritos = res?.body ?? res?.data
        if (Array.isArray(distritos) && distritos.length > 0) {
          dispatch(setDistritos(distritos))
          return true
        }
        dispatch(setDistritos([]))
        return false
      })
      .catch(async () => {
        dispatch(setDistritos([]))
        return false
      })
      .finally(() => {
        if (loader) dispatch(generalShowLoader(false))
      })
  }

export const setTiposContingencia = (value: any) => ({
  type: CATALOGOS_TYPES.CATALOGO_SET_TIPOS_CONTINGENCIA,
  payload: value,
})
export const setTiposLibroVentas = (value: any) => ({
  type: CATALOGOS_TYPES.CATALOGO_SET_TIPOS_LIBRO_VENTAS,
  payload: value,
})

export const getTiposContingencia =
  (forzar: boolean = false) =>
  async (dispatch: Dispatch<CatalogoAction>, getState: () => State) => {
    if (catalogoVigente('tiposContingencia', getState(), forzar)) return true

    return await unaSolaPeticion('tiposContingencia', async () => {
      try {
        const respuesta: any = await http.get(ENDPOINTS.GET_TIPO_CONTINGENCIA)
        const tipos = respuesta?.body ?? respuesta?.data
        if (Array.isArray(tipos) && tipos.length > 0) {
          dispatch(setTiposContingencia(tipos))
          sellarCatalogo('tiposContingencia', getState())
          return true
        }
        return false
      } catch {
        return false
      }
    })
  }

export const getTiposLibroVentas =
  (forzar: boolean = false) =>
  async (dispatch: Dispatch<CatalogoAction>, getState: () => State) => {
    if (catalogoVigente('tiposLibroVentas', getState(), forzar)) return true

    return await unaSolaPeticion('tiposLibroVentas', async () => {
      try {
        const respuesta: any = await http.get(ENDPOINTS.GET_TIPO_LIBRO_VENTAS)
        const tipos = respuesta?.body ?? respuesta?.data
        if (Array.isArray(tipos) && tipos.length > 0) {
          dispatch(
            setTiposLibroVentas(
              tipos.map((tipo: any) => ({
                codigo: `${tipo?.codigo ?? ''}`,
                codigo_nombre: `${tipo?.valor ?? ''}`.toUpperCase(),
              }))
            )
          )
          sellarCatalogo('tiposLibroVentas', getState())
          return true
        }
        return false
      } catch {
        return false
      }
    })
  }

export const cargarDistritosDelCliente =
  (departamento: any, municipio: any, loader: boolean = false) =>
  async (dispatch: Dispatch<CatalogoAction>) => {
    const codigoDepartamento = codigoTerritorial(departamento)
    if (!codigoDepartamento) {
      dispatch(setDistritos([]))
      return false
    }
    if (codigoTerritorial(municipio)) {
      const hubo = await (dispatch(
        getDistritos(codigoDepartamento, municipio, loader) as any
      ) as any)
      if (hubo) return true
    }
    return await (dispatch(getDistritos(codigoDepartamento, '', loader) as any) as any)
  }

const mapaDistritoMunicipioEnUnaLlamada = async (
  codigoDepartamento: string
): Promise<Record<string, string> | null> => {
  try {
    const respuesta: any = await http.post(ENDPOINTS.GET_DISTRITO_MUNICIPIO, {
      departamento: codigoDepartamento,
    })
    const distritos = respuesta?.body ?? respuesta?.data
    if (!Array.isArray(distritos) || distritos.length === 0) return null

    const mapa: Record<string, string> = {}
    distritos.forEach((distrito: any) => {
      const codigoDistrito = codigoTerritorial(distrito?.codigo)
      const codigoMunicipio = codigoTerritorial(distrito?.municipio_v2)
      if (codigoDistrito && codigoMunicipio) mapa[codigoDistrito] = codigoMunicipio
    })

    return Object.keys(mapa).length > 0 ? mapa : null
  } catch {
    return null
  }
}

export const getMapaDistritoMunicipio =
  (departamento: any) => async (dispatch: Dispatch<CatalogoAction>, getState: () => State) => {
    const codigoDepartamento = codigoTerritorial(departamento)
    if (!codigoDepartamento) return false

    const mapaEnCache = getState().catalogos.mapaDistritoMunicipio?.[codigoDepartamento]
    if (mapaEnCache && Object.keys(mapaEnCache).length > 0) return true

    const mapaDeUnaLlamada = await mapaDistritoMunicipioEnUnaLlamada(codigoDepartamento)
    if (mapaDeUnaLlamada) {
      dispatch(setMapaDistritoMunicipio(codigoDepartamento, mapaDeUnaLlamada))
      return true
    }

    const nombreDepartamento = (getState().catalogos.departamentos ?? []).find(
      (dep: any) => codigoTerritorial(dep?.codigo) === codigoDepartamento
    )?.valor

    try {
      const respuestaMunicipios: any = await http.post(ENDPOINTS.GET_MUNICIPIOS, {
        codigo: codigoDepartamento,
        valor: nombreDepartamento ?? '',
      })
      const municipios = respuestaMunicipios?.body ?? respuestaMunicipios?.data
      if (!Array.isArray(municipios) || municipios.length === 0) return false

      const mapa: Record<string, string> = {}
      const respuestas = await Promise.all(
        municipios.map(async (municipio: any) => {
          const codigoMunicipio = codigoTerritorial(municipio?.codigo)
          if (!codigoMunicipio) return {codigoMunicipio: '', distritos: []}
          const respuestaDistritos: any = await http.post(ENDPOINTS.GET_DISTRITO, {
            departamento: codigoDepartamento,
            municipio: codigoMunicipio,
          })
          const distritos = respuestaDistritos?.body ?? respuestaDistritos?.data
          return {codigoMunicipio, distritos: Array.isArray(distritos) ? distritos : []}
        })
      )

      respuestas.forEach(({codigoMunicipio, distritos}) => {
        if (!codigoMunicipio) return
        distritos.forEach((distrito: any) => {
          const codigoDistrito = codigoTerritorial(distrito?.codigo)
          if (codigoDistrito) mapa[codigoDistrito] = codigoMunicipio
        })
      })

      if (Object.keys(mapa).length === 0) return false
      dispatch(setMapaDistritoMunicipio(codigoDepartamento, mapa))
      return true
    } catch {
      return false
    }
  }

export const getPlanFacturas = () => async (dispatch: Dispatch<CatalogoAction>) => {
  return await http
    .get(ENDPOINTS.GET_PLANFACTURAS)
    .then(async (res: any) => {
      if (res.data) {
        dispatch(setPlanFacturas(res.data))
        return true
      } else {
        dispatch(
          generalOpenModalError('No hay plan de facturas disponibles para realizar operaciones')
        )
        return false
      }
    })
    .catch(async (err: any) => {
      let message = 'Error al cargar catalogo de plan de facturas, intente nuevamente'
      await dispatch(
        generalOpenModalError(err?.response?.data?.message ?? message, err?.response?.data?.status)
      )
      return false
    })
}
export const getActividadesEconomicas =
  (forzar: boolean = false) =>
  async (dispatch: Dispatch<CatalogoAction>, getState: () => State) => {
    if (catalogoVigente('actividadesEconomicas', getState(), forzar)) return true
    const enCurso = enVuelo('actividadesEconomicas')
    if (enCurso) return await enCurso

    const peticion = http
      .get(ENDPOINTS.GET_ACTIVIDAD_ECONOMICA)
      .then(async (res: any) => {
        if (res.body) {
          let response = res?.body?.filter((act: any) => act?.codigo?.trim() !== '')
          dispatch(setActividadesEconomicas(response))
          sellarCatalogo('actividadesEconomicas', getState())
          return true
        } else {
          dispatch(generalOpenModalError('No hay actividades económicas para realizar operaciones'))
          return false
        }
      })
      .catch(async (err: any) => {
        let message = 'Error al cargar catalogo de actividades económicas, intente nuevamente'
        await dispatch(
          generalOpenModalError(
            err?.response?.data?.message ?? message,
            err?.response?.data?.status
          )
        )
        return false
      })
    return await registrarPeticion('actividadesEconomicas', peticion)
  }
export const getTipoEstablecimiento =
  (forzar: boolean = false) =>
  async (dispatch: Dispatch<CatalogoAction>, getState: () => State) => {
    if (catalogoVigente('tipoEstablecimientos', getState(), forzar)) return true
    const enCurso = enVuelo('tipoEstablecimientos')
    if (enCurso) return await enCurso

    const peticion = http
      .get(ENDPOINTS.GET_TIPO_ESTABLECIMIENTOS)
      .then(async (res: any) => {
        if (res.body) {
          dispatch(setTipoEstablecimientos(res.body))
          sellarCatalogo('tipoEstablecimientos', getState())
          return true
        } else {
          dispatch(
            generalOpenModalError('No hay tipos de establecimientos para realizar operaciones')
          )
          return false
        }
      })
      .catch(async (err: any) => {
        let message = 'Error al cargar catalogo de tipos de establecimientos, intente nuevamente'
        await dispatch(
          generalOpenModalError(
            err?.response?.data?.message ?? message,
            err?.response?.data?.status
          )
        )
        return false
      })
    return await registrarPeticion('tipoEstablecimientos', peticion)
  }

export const getTipoPersona =
  (forzar: boolean = false) =>
  async (dispatch: Dispatch<CatalogoAction>, getState: () => State) => {
    if (catalogoVigente('tipoPersona', getState(), forzar)) return true
    const enCurso = enVuelo('tipoPersona')
    if (enCurso) return await enCurso

    const peticion = http
      .get(ENDPOINTS.GET_TIPO_PERSONA)
      .then(async (res: any) => {
        if (Array.isArray(res.data) && res.data.length > 0) {
          dispatch(setTipoPersona(res.data))
          sellarCatalogo('tipoPersona', getState())
          return true
        } else {
          dispatch(generalOpenModalError('No hay tipos de persona disponibles'))
          return false
        }
      })
      .catch(async (err: any) => {
        let message = 'Error al cargar catalogo de tipos de persona, intente nuevamente'
        await dispatch(
          generalOpenModalError(
            err?.response?.data?.message ?? message,
            err?.response?.data?.status
          )
        )
        return false
      })
    return await registrarPeticion('tipoPersona', peticion)
  }

export const getSuscripciones =
  () => async (dispatch: Dispatch<CatalogoAction>) => {
    return await http
      .get(ENDPOINTS.GET_SUSCRIPCIONES)
      .then((res: any) => {
        if (res?.body) {
          dispatch(setPlanFacturas(res?.body));
          return true;
        } else {
          dispatch(generalOpenModalError('No hay tipos de suscripciones para realizar operaciones'))
          return false;
        }
      })
      .catch((err: any) => {
        let message = 'Error al cargar catalogo de tipos de suscripciones, intente nuevamente'
        dispatch(generalOpenModalError(err?.response?.data?.message ?? message, err?.response?.data?.status))
        return false;
      })
  }

export const getUnidadesMedida = (forzar: boolean = false) => async(dispatch: Dispatch<CatalogoAction>, getState: () => State) => {
  if (catalogoVigente('unidadesMedida', getState(), forzar)) return true

  return await unaSolaPeticion('unidadesMedida', async () => {
    try {
      const response: any = await http.get(ENDPOINTS.GET_UNIDAD_MEDIDA)
      if(Array.isArray(response.body) && response.body.length > 0){
        dispatch(setUnidadesMedida(response.body))
        sellarCatalogo('unidadesMedida', getState())
        return true
      } else {
        dispatch(generalOpenModalError(response?.message ?? 'No se encontraron unidades de medida para realizar operaciones, recarga la página'))
        return false
      }
    } catch (error: any) {
      let message = 'Ocurrió un error al cargar el catálogo de Unidades de Medida'
      dispatch(generalOpenModalError(error?.response?.data?.message ?? message, error?.response?.data?.status))
    }
  })
}

export const getImpuestosProducto = (forzar: boolean = false) => async(dispatch: Dispatch<CatalogoAction>, getState: () => State) => {
  if (catalogoVigente('impuestosProducto', getState(), forzar)) return true

  return await unaSolaPeticion('impuestosProducto', async () => {
    try {
      const response: any = await http.get(ENDPOINTS.GET_IMPUESTOS_PRODUCTO)
      if(Array.isArray(response.body) && response.body.length > 0){
        dispatch(setImpuestosDistintosAlIva(response.body))
        sellarCatalogo('impuestosProducto', getState())
        return true
      } else {
        dispatch(generalOpenModalError(response?.message ?? 'No se encontraron impuestos para realizar operaciones, recarga la página'))
        return false
      }
    } catch (error: any) {
      let message = 'Ocurrió un error al cargar el catálogo de Impuestos'
      dispatch(generalOpenModalError(error?.response?.data?.message ?? message, error?.response?.data?.status))
    }
  })
}


export const getTiposDocumento =
  (forzar: boolean = false) =>
  async (dispatch: Dispatch<CatalogoAction>, getState: () => State) => {
    if (catalogoVigente('tipoDocumento', getState(), forzar)) return true
    const enCurso = enVuelo('tipoDocumento')
    if (enCurso) return await enCurso

    const peticion = http
      .get(ENDPOINTS.GET_TIPO_IDENTIFICACION)
      .then(async (res: any) => {
        if (res.body) {
          const docs = res.body.sort((a: any, b: any) => {
            const nombreA = a?.codigo?.toLowerCase()
            const nombreB = b?.codigo.toLowerCase()
            return nombreA < nombreB ? -1 : nombreA > nombreB ? 1 : 0
          })
          dispatch(setTiposDocumento(docs))
          sellarCatalogo('tipoDocumento', getState())
          return true
        } else {
          dispatch(generalOpenModalError(res?.message ?? 'No hay tipos de documento para realizar operaciones'))
          return false
        }
      })
      .catch(async (err: any) => {
        let message = 'Error al cargar catalogo de tipos de documento'
        await dispatch(generalOpenModalError(err?.response?.data?.message ?? message, err?.response?.data?.status))
        return false
      })
    return await registrarPeticion('tipoDocumento', peticion)
  }
export const getTiposDocumentoEmision =
  (forzar: boolean = false) =>
  async (dispatch: Dispatch<CatalogoAction>, getState: () => State) => {
    if (catalogoVigente('tipoDocumentosEmision', getState(), forzar)) return true
    const enCurso = enVuelo('tipoDocumentosEmision')
    if (enCurso) return await enCurso

    const peticion = http
      .get(ENDPOINTS.GET_TIPO_DOCUMENTOS)
      .then(async (res: any) => {
        if (res.data) {
          dispatch(setTiposDocumentoEmision(res.data))
          sellarCatalogo('tipoDocumentosEmision', getState())
          return true
        } else {
          dispatch(generalOpenModalError(res?.message ?? 'No hay tipos de documento para realizar operaciones'))
          return false
        }
      })
      .catch(async (err: any) => {
        let message = 'Error al cargar catalogo de tipos de documento'
        await dispatch(generalOpenModalError(err?.response?.data?.message ?? message,err?.response?.data?.status))
        return false
      })
    return await registrarPeticion('tipoDocumentosEmision', peticion)
  }
export const postNombreDepartamento = async (codigoDepartamento: string) => {
  let message = 'Error al obtener información del departamento'
  return await http
    .post(ENDPOINTS.GET_NOMBRE_DEPARTAMENTO, {codigo: codigoDepartamento})
    .then((res: any) => {
      if (res?.body && res.body.length > 0 && res.body[0])
        return {body: res.body[0], error: false, message: ''}
      else return {body: null, error: true, message: res?.message ?? message}
    })
    .catch((err: any) => {
      return {body: null, error: true, message: err?.response?.data?.message ?? message}
    })
}
export const postNombreMunicipio = async (codigoDepartamento: string, codigoMunicipio: string) => {
  let message = 'Error al obtener información del municipio'
  return await http
    .post(ENDPOINTS.GET_NOMBRE_MUNICIPIO, {codigoDepartamento, codigoMunicipio})
    .then((res: any) => {
      if (res?.body && res.body.length > 0 && res.body[0])
        return {body: res.body[0], error: false, message: ''}
      else return {body: null, error: true, message: res?.message ?? message}
    })
    .catch((err: any) => {
      return {body: null, error: true, message: err?.response?.data?.message ?? message}
    })
}

export const postNombreDistrito = async (
  codigoDepartamento: string,
  codigoMunicipio: string,
  codigoDistrito: string
) => {
  const departamento = codigoTerritorial(codigoDepartamento)
  const municipio = codigoTerritorial(codigoMunicipio)
  const distrito = codigoTerritorial(codigoDistrito)
  if (!departamento || !distrito) return {body: null, error: true, message: ''}

  return await http
    .post(ENDPOINTS.GET_DISTRITO, {
      departamento,
      ...(municipio ? {municipio} : {}),
    })
    .then((res: any) => {
      const distritos = res?.body ?? res?.data
      if (!Array.isArray(distritos)) return {body: null, error: true, message: ''}
      const encontrado = distritos.find(
        (item: any) => codigoTerritorial(item?.codigo) === distrito
      )
      return {body: encontrado ?? null, error: !encontrado, message: ''}
    })
    .catch(() => ({body: null, error: true, message: ''}))
}

export const getBotonesAyuda =
  () => async (dispatch: Dispatch<CatalogoAction>) => {
    return await http.get(ENDPOINTS.GET_BOTONES_AYUDA)
    .then(async (res: any) => {
      if(res.data){
        dispatch(setCatalogoBotonesAyuda(res.data));
        return true
      } else {
        dispatch(generalOpenModalError(res.message || 'Ocurrió un error al cargar los botones de ayuda al usuario.'))
      }
    })
    .catch(async (err: any) => {
      let message = 'Error al cargar catalogo de tipos de ayuda, intente nuevamente'
      await dispatch(generalOpenModalError(err?.response?.data?.message ?? message, err?.response?.data?.status))
      return false;
    })
  }

export const getCondicionOperacionCAT016 =
  (forzar: boolean = false) =>
  async (dispatch: Dispatch<CatalogoAction>, getState: () => State) => {
    if (catalogoVigente('condicionOperacion', getState(), forzar)) return true
    const enCurso = enVuelo('condicionOperacion')
    if (enCurso) return await enCurso

    const peticion = http.get(ENDPOINTS.GET_CONDICION_OPERACION_CAT016).then(async (res: any) => {
        if (res.body) {
          dispatch(setCatalogoCondicionOperacionCat016(res.body))
          sellarCatalogo('condicionOperacion', getState())
          return true
        }
        dispatch(generalOpenModalError(res?.message ?? 'El catálogo condición de la operación no posee datos'))
        return false
      })
      .catch(async (err: any) => {
        let message = 'Error al cargar catálogo de condición de la operación'
        await dispatch(generalOpenModalError(err?.response?.data?.message || message, err?.response?.data?.status))
        return false
      })
    return await registrarPeticion('condicionOperacion', peticion)
  }

  
export const getFormasPago = (forzar: boolean = false) => async(dispatch: Dispatch<CatalogoAction>, getState: () => State) => {
  if (catalogoVigente('formasPago', getState(), forzar)) return true

  return await unaSolaPeticion('formasPago', async () => {
    try {
      const response: any = await http.get(ENDPOINTS.GET_FORMA_PAGO)
      if(Array.isArray(response.body) && response.body.length > 0){
        dispatch(handleSetFormasPago(response.body))
        sellarCatalogo('formasPago', getState())
        return true
      } else {
        dispatch(generalOpenModalError(response?.message ?? 'No se encontraron formas de pago, recarga la página'))
        return false
      }
    } catch (error: any) {
      let message = 'Ocurrió un error al cargar el catálogo de formas de pago'
      dispatch(generalOpenModalError(error?.response?.data?.message ?? message, error?.response?.data?.status))
    }
  })
}

export const getCondicionesPago = (forzar: boolean = false) => async(dispatch: Dispatch<CatalogoAction>, getState: () => State) => {
  if (catalogoVigente('condicionesPago', getState(), forzar)) return true

  return await unaSolaPeticion('condicionesPago', async () => {
    try {
      const response: any = await http.get(ENDPOINTS.GET_CONDICION_OPERACION_CAT016)
      if(Array.isArray(response.body) && response.body.length > 0){
        dispatch(handleSetCondicionesPago(response.body))
        sellarCatalogo('condicionesPago', getState())
        return true
      } else {
        dispatch(generalOpenModalError(response?.message ?? 'No se encontraron condiciones de pago, recarga la página'))
        return false
      }
    } catch (error: any) {
      let message = 'Ocurrió un error al cargar el catálogo de condiciones de pago'
      dispatch(generalOpenModalError(error?.response?.data?.message ?? message, error?.response?.data?.status))
    }
  })
}

export const getPlazosPago = (forzar: boolean = false) => async(dispatch: Dispatch<CatalogoAction>, getState: () => State) => {
  if (catalogoVigente('plazosPago', getState(), forzar)) return true

  return await unaSolaPeticion('plazosPago', async () => {
    try {
      const response: any = await http.get(ENDPOINTS.GET_PLAZO_PAGO)
      if(Array.isArray(response.body) && response.body.length > 0){
        dispatch(handleSetPlazosPago(response.body))
        sellarCatalogo('plazosPago', getState())
        return true
      } else {
        dispatch(generalOpenModalError(response?.message ?? 'No se encontraron plazos de pago, recarga la página'))
        return false
      }
    } catch (error: any) {
      let message = 'Ocurrió un error al cargar el catálogo de plazos de pago'
      dispatch(generalOpenModalError(error?.response?.data?.message ?? message, error?.response?.data?.status))
    }
  })
}

export const getTiposCargoMembresia = (forzar: boolean = false) => async(dispatch: Dispatch<CatalogoAction>, getState: () => State) => {
  if (catalogoVigente('tipoCargosMembresia', getState(), forzar)) return true

  return await unaSolaPeticion('tipoCargosMembresia', async () => {
    try {
      const response: any = await http.get(ENDPOINTS.GET_TIPOS_CARGOS_MEMBRESIA)
      if(Array.isArray(response.body) && response.body.length > 0){
        dispatch(handleSetTipoCargosMembresia(response.body))
        sellarCatalogo('tipoCargosMembresia', getState())
        return true
      } else {
        dispatch(generalOpenModalError(response?.message ?? 'No se encontraron cargos de membresia, recarga la página'))
        return false
      }
    } catch (error: any) {
      let message = 'Ocurrió un error al cargar el catálogo de cargos de membresia'
      dispatch(generalOpenModalError(error?.response?.data?.message ?? message, error?.response?.data?.status))
    }
  })
}

export const getCatalogosProductos = (codigoempresa: string, idsucursal: string, forzar: boolean = false) => async(dispatch: Dispatch<CatalogoAction>, getState: () => State) => {
  if (catalogoVigente('productos', getState(), forzar)) return true

  const params = {
    codigoempresa,
    idsucursal
  }

  return await unaSolaPeticion('productos', async () => {
    try {
      const response: any = await http.get(ENDPOINTS.GET_PRODUCTOS, params)
      if(Array.isArray(response.data)){
        dispatch(handleSetProductos(response.data))
        sellarCatalogo('productos', getState())
        return true;
      }
      return false;
    } catch (error: any) {
      let message = 'Ocurrió un error al cargar el catálogo de productos'
      dispatch(generalOpenModalError(error?.response?.data?.message ?? message, error?.response?.data?.status))
    }
  })
}



export const recargarCatalogosDeLaNormativa =
  () => async (dispatch: Dispatch<any>) => {
    olvidarCatalogosCargados()

    await Promise.all([
      dispatch(getFormasPago(true) as any),
      dispatch(getCondicionesPago(true) as any),
      dispatch(getPlazosPago(true) as any),
      dispatch(getUnidadesMedida(true) as any),
      dispatch(getImpuestosProducto(true) as any),
      dispatch(getDepartamentos(true) as any),
      dispatch(getTipoEstablecimiento(true) as any),
      dispatch(getTipoPersona(true) as any),
      dispatch(getTiposDocumento(true) as any),
      dispatch(getTiposDocumentoEmision(true) as any),
      dispatch(getCondicionOperacionCAT016(true) as any),
      dispatch(getActividadesEconomicas(true) as any),
      dispatch(getBancos(true) as any),
    ])
  }
